import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import { QBConnectButton } from '@/components/QBConnectButton';
import { 
  Loader2, 
  Settings, 
  CreditCard,
  Calendar,
  MessageSquare,
  Mail,
  Calculator,
  CheckCircle,
  XCircle,
  ExternalLink,
  Save,
  TestTube,
  Eye,
  EyeOff
} from 'lucide-react';

export default function IntegrationsPage() {
  const { api, hasRole } = useAuth();
  const { t, lang } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});

  // Form states for each integration
  const [twilio, setTwilio] = useState({
    enabled: false,
    account_sid: '',
    auth_token: '',
    phone_number: ''
  });

  const [stripe, setStripe] = useState({
    enabled: false,
    publishable_key: '',
    secret_key: '',
    webhook_secret: ''
  });

  const [quickbooks, setQuickbooks] = useState({
    enabled: false,
    client_id: '',
    client_secret: ''
  });

  const [email, setEmail] = useState({
    enabled: false,
    provider: '',
    api_key: '',
    from_address: '',
    from_name: 'CGC Services',
    daily_limit: 299,
    smtp_host: '',
    smtp_port: '',
    smtp_username: '',
    smtp_password: ''
  });

  const [emailUsage, setEmailUsage] = useState(null);

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get('/integrations/settings');
      setSettings(res.data);
      
      // Populate forms from settings
      if (res.data.twilio) {
        setTwilio(prev => ({
          ...prev,
          enabled: res.data.twilio.enabled,
          phone_number: res.data.twilio.phone_number || ''
        }));
      }
      if (res.data.stripe) {
        setStripe(prev => ({
          ...prev,
          enabled: res.data.stripe.enabled,
          publishable_key: res.data.stripe.publishable_key || ''
        }));
      }
      if (res.data.email) {
        setEmail(prev => ({
          ...prev,
          enabled: res.data.email.enabled || false,
          provider: res.data.email.provider || '',
          from_address: res.data.email.from_address || '',
          from_name: res.data.email.from_name || 'CGC Services',
          daily_limit: res.data.email.daily_limit || 299,
        }));
      }

      // Load email usage for today
      try {
        const u = await api.get('/integrations/email/usage');
        setEmailUsage(u.data);
      } catch {}
    } catch (err) {
      console.error('Failed to load settings:', err);
      toast.error('Failed to load integration settings');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async (provider, data) => {
    setSaving(true);
    try {
      const updateData = {};
      
      if (provider === 'twilio') {
        updateData.twilio_enabled = data.enabled;
        if (data.account_sid) updateData.twilio_account_sid = data.account_sid;
        if (data.auth_token) updateData.twilio_auth_token = data.auth_token;
        if (data.phone_number) updateData.twilio_phone_number = data.phone_number;
      } else if (provider === 'stripe') {
        updateData.stripe_enabled = data.enabled;
        if (data.publishable_key) updateData.stripe_publishable_key = data.publishable_key;
        if (data.secret_key) updateData.stripe_secret_key = data.secret_key;
        if (data.webhook_secret) updateData.stripe_webhook_secret = data.webhook_secret;
      } else if (provider === 'quickbooks') {
        updateData.quickbooks_enabled = data.enabled;
        if (data.client_id) updateData.quickbooks_client_id = data.client_id;
        if (data.client_secret) updateData.quickbooks_client_secret = data.client_secret;
      } else if (provider === 'email') {
        updateData.email_enabled = data.enabled;
        if (data.provider) updateData.email_provider = data.provider;
        if (data.api_key) updateData.email_api_key = data.api_key;
        if (data.from_address) updateData.email_from_address = data.from_address;
        if (data.from_name) updateData.email_from_name = data.from_name;
        if (data.daily_limit) updateData.email_daily_limit = parseInt(data.daily_limit);
        if (data.smtp_host) updateData.smtp_host = data.smtp_host;
        if (data.smtp_port) updateData.smtp_port = parseInt(data.smtp_port);
        if (data.smtp_username) updateData.smtp_username = data.smtp_username;
        if (data.smtp_password) updateData.smtp_password = data.smtp_password;
      }

      await api.put('/integrations/settings', updateData);
      toast.success(lang === 'ru' ? 'Настройки сохранены' : 'Settings saved');
      loadSettings();
    } catch (err) {
      console.error('Save error:', err);
      toast.error(lang === 'ru' ? 'Ошибка сохранения' : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (provider) => {
    setTesting(provider);
    try {
      const res = await api.post(`/integrations/test/${provider}`);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error('Test failed');
    } finally {
      setTesting(null);
    }
  };

  const toggleShowSecret = (key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!hasRole('admin')) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        {lang === 'ru' ? 'Только администратор может настраивать интеграции' : 'Only admin can configure integrations'}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="h-6 w-6" />
          {lang === 'ru' ? 'Настройки интеграций' : 'Integration Settings'}
        </h1>
        <p className="text-muted-foreground">
          {lang === 'ru' 
            ? 'Подключите внешние сервисы для расширения функционала CRM'
            : 'Connect external services to extend CRM functionality'}
        </p>
      </div>

      <Accordion type="multiple" className="space-y-4">
        {/* Twilio SMS */}
        <AccordionItem value="twilio" className="border rounded-lg">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-red-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold">Twilio SMS</h3>
                <p className="text-sm text-muted-foreground">
                  {lang === 'ru' ? 'SMS напоминания клиентам' : 'SMS reminders to clients'}
                </p>
              </div>
              {settings?.twilio?.configured ? (
                <Badge className="ml-auto bg-green-100 text-green-700">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {lang === 'ru' ? 'Настроено' : 'Configured'}
                </Badge>
              ) : (
                <Badge variant="outline" className="ml-auto">
                  {lang === 'ru' ? 'Не настроено' : 'Not configured'}
                </Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="space-y-4 pt-4">
              <div className="flex items-center justify-between">
                <Label>{lang === 'ru' ? 'Включить' : 'Enable'}</Label>
                <Switch 
                  checked={twilio.enabled} 
                  onCheckedChange={(v) => setTwilio(prev => ({...prev, enabled: v}))}
                />
              </div>
              <div className="grid gap-4">
                <div>
                  <Label>Account SID</Label>
                  <div className="flex gap-2">
                    <Input 
                      type={showSecrets.twilio_sid ? "text" : "password"}
                      value={twilio.account_sid}
                      onChange={(e) => setTwilio(prev => ({...prev, account_sid: e.target.value}))}
                      placeholder="ACxxxxx..."
                    />
                    <Button variant="ghost" size="icon" onClick={() => toggleShowSecret('twilio_sid')}>
                      {showSecrets.twilio_sid ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>Auth Token</Label>
                  <div className="flex gap-2">
                    <Input 
                      type={showSecrets.twilio_token ? "text" : "password"}
                      value={twilio.auth_token}
                      onChange={(e) => setTwilio(prev => ({...prev, auth_token: e.target.value}))}
                      placeholder="xxxxx..."
                    />
                    <Button variant="ghost" size="icon" onClick={() => toggleShowSecret('twilio_token')}>
                      {showSecrets.twilio_token ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label>{lang === 'ru' ? 'Номер телефона' : 'Phone Number'}</Label>
                  <Input 
                    value={twilio.phone_number}
                    onChange={(e) => setTwilio(prev => ({...prev, phone_number: e.target.value}))}
                    placeholder="+1234567890"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={() => handleSave('twilio', twilio)} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  {lang === 'ru' ? 'Сохранить' : 'Save'}
                </Button>
                <Button variant="outline" onClick={() => handleTest('twilio')} disabled={testing === 'twilio'}>
                  {testing === 'twilio' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TestTube className="h-4 w-4 mr-2" />}
                  {lang === 'ru' ? 'Тест' : 'Test'}
                </Button>
                <Button variant="ghost" asChild>
                  <a href="https://console.twilio.com" target="_blank" rel="noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Twilio Console
                  </a>
                </Button>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Stripe Payments */}
        <AccordionItem value="stripe" className="border rounded-lg">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-purple-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold">Stripe</h3>
                <p className="text-sm text-muted-foreground">
                  {lang === 'ru' ? 'Онлайн оплата картой' : 'Online card payments'}
                </p>
              </div>
              {settings?.stripe?.configured ? (
                <Badge className="ml-auto bg-green-100 text-green-700">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {lang === 'ru' ? 'Настроено' : 'Configured'}
                </Badge>
              ) : (
                <Badge variant="outline" className="ml-auto">
                  {lang === 'ru' ? 'Не настроено' : 'Not configured'}
                </Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="space-y-4 pt-4">
              <div className="flex items-center justify-between">
                <Label>{lang === 'ru' ? 'Включить' : 'Enable'}</Label>
                <Switch 
                  checked={stripe.enabled} 
                  onCheckedChange={(v) => setStripe(prev => ({...prev, enabled: v}))}
                />
              </div>
              <div className="grid gap-4">
                <div>
                  <Label>Publishable Key</Label>
                  <Input 
                    value={stripe.publishable_key}
                    onChange={(e) => setStripe(prev => ({...prev, publishable_key: e.target.value}))}
                    placeholder="pk_live_xxxxx..."
                  />
                </div>
                <div>
                  <Label>Secret Key</Label>
                  <div className="flex gap-2">
                    <Input 
                      type={showSecrets.stripe_secret ? "text" : "password"}
                      value={stripe.secret_key}
                      onChange={(e) => setStripe(prev => ({...prev, secret_key: e.target.value}))}
                      placeholder="sk_live_xxxxx..."
                    />
                    <Button variant="ghost" size="icon" onClick={() => toggleShowSecret('stripe_secret')}>
                      {showSecrets.stripe_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={() => handleSave('stripe', stripe)} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  {lang === 'ru' ? 'Сохранить' : 'Save'}
                </Button>
                <Button variant="outline" onClick={() => handleTest('stripe')} disabled={testing === 'stripe'}>
                  {testing === 'stripe' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <TestTube className="h-4 w-4 mr-2" />}
                  {lang === 'ru' ? 'Тест' : 'Test'}
                </Button>
                <Button variant="ghost" asChild>
                  <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Stripe Dashboard
                  </a>
                </Button>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* QuickBooks */}
        <AccordionItem value="quickbooks" className="border rounded-lg">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Calculator className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold">QuickBooks</h3>
                <p className="text-sm text-muted-foreground">
                  {lang === 'ru' ? 'Синхронизация с бухгалтерией' : 'Accounting sync'}
                </p>
              </div>
              {settings?.quickbooks?.connected ? (
                <Badge className="ml-auto bg-green-100 text-green-700">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {lang === 'ru' ? 'Подключено' : 'Connected'}
                </Badge>
              ) : (
                <Badge variant="outline" className="ml-auto">
                  {lang === 'ru' ? 'Не настроено' : 'Not configured'}
                </Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="space-y-4 pt-4">
              {/* OAuth Connection Status */}
              <QBConnectButton
                settings={{
                  quickbooks_enabled: settings?.quickbooks?.enabled,
                  quickbooks_connected_at: settings?.quickbooks?.connected_at,
                  quickbooks_company_name: settings?.quickbooks?.company_name,
                }}
                onRefresh={loadSettings}
              />

              <Separator />

              {/* Client credentials (needed before OAuth) */}
              <div className="grid gap-4">
                <div>
                  <Label>Client ID</Label>
                  <Input
                    value={quickbooks.client_id}
                    onChange={(e) => setQuickbooks(prev => ({...prev, client_id: e.target.value}))}
                    placeholder="ABxxxxx..."
                  />
                </div>
                <div>
                  <Label>Client Secret</Label>
                  <div className="flex gap-2">
                    <Input
                      type={showSecrets.qb_secret ? "text" : "password"}
                      value={quickbooks.client_secret}
                      onChange={(e) => setQuickbooks(prev => ({...prev, client_secret: e.target.value}))}
                      placeholder="xxxxx..."
                    />
                    <Button variant="ghost" size="icon" onClick={() => toggleShowSecret('qb_secret')}>
                      {showSecrets.qb_secret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button onClick={() => handleSave('quickbooks', quickbooks)} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  {lang === 'ru' ? 'Сохранить' : 'Save'}
                </Button>
                <Button variant="ghost" asChild>
                  <a href="https://developer.intuit.com" target="_blank" rel="noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Developer Portal
                  </a>
                </Button>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Email */}
        <AccordionItem value="email" className="border rounded-lg">
          <AccordionTrigger className="px-4 hover:no-underline">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Mail className="h-5 w-5 text-amber-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold">Email</h3>
                <p className="text-sm text-muted-foreground">
                  {lang === 'ru' ? 'Отправка уведомлений и квитанций' : 'Send notifications and receipts'}
                </p>
              </div>
              {settings?.email?.configured ? (
                <Badge className="ml-auto bg-green-100 text-green-700">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {settings.email.provider?.toUpperCase()}
                </Badge>
              ) : (
                <Badge variant="outline" className="ml-auto">
                  {lang === 'ru' ? 'Не настроено' : 'Not configured'}
                </Badge>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div className="space-y-4 pt-4">
              {/* Enabled toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div>
                  <Label className="text-base">{lang === 'ru' ? 'Email включён' : 'Email enabled'}</Label>
                  <p className="text-xs text-muted-foreground">
                    {lang === 'ru' ? 'Разрешить отправку email из CRM' : 'Allow sending email from CRM'}
                  </p>
                </div>
                <Switch
                  checked={email.enabled}
                  onCheckedChange={(v) => setEmail(prev => ({...prev, enabled: v}))}
                />
              </div>

              {/* Usage today */}
              {emailUsage && (
                <div className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-900/20">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      {lang === 'ru' ? 'Отправлено сегодня' : 'Sent today'}
                    </span>
                    <span className="font-mono font-semibold">
                      {emailUsage.count} / {emailUsage.limit}
                    </span>
                  </div>
                  <div className="mt-2 h-2 bg-muted rounded overflow-hidden">
                    <div
                      className={`h-full ${emailUsage.count >= emailUsage.limit ? 'bg-red-500' : emailUsage.count / emailUsage.limit > 0.8 ? 'bg-amber-500' : 'bg-green-500'}`}
                      style={{ width: `${Math.min(100, (emailUsage.count / emailUsage.limit) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              <div>
                <Label>{lang === 'ru' ? 'Провайдер' : 'Provider'}</Label>
                <Select value={email.provider} onValueChange={(v) => setEmail(prev => ({...prev, provider: v}))}>
                  <SelectTrigger>
                    <SelectValue placeholder={lang === 'ru' ? 'Выберите провайдера' : 'Select provider'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="resend">Resend {lang === 'ru' ? '(рекомендуется)' : '(recommended)'}</SelectItem>
                    <SelectItem value="sendgrid">SendGrid</SelectItem>
                    <SelectItem value="smtp">SMTP</SelectItem>
                  </SelectContent>
                </Select>
                {email.provider === 'resend' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {lang === 'ru'
                      ? 'Получите API ключ на resend.com — 3000 писем/мес бесплатно, без брендинга'
                      : 'Get API key at resend.com — 3000 emails/mo free, no branding'}
                  </p>
                )}
              </div>
              
              {email.provider && email.provider !== 'smtp' && (
                <div>
                  <Label>API Key</Label>
                  <div className="flex gap-2">
                    <Input 
                      type={showSecrets.email_key ? "text" : "password"}
                      value={email.api_key}
                      onChange={(e) => setEmail(prev => ({...prev, api_key: e.target.value}))}
                      placeholder="xxxxx..."
                    />
                    <Button variant="ghost" size="icon" onClick={() => toggleShowSecret('email_key')}>
                      {showSecrets.email_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}

              {email.provider === 'smtp' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>SMTP Host</Label>
                      <Input 
                        value={email.smtp_host}
                        onChange={(e) => setEmail(prev => ({...prev, smtp_host: e.target.value}))}
                        placeholder="smtp.example.com"
                      />
                    </div>
                    <div>
                      <Label>Port</Label>
                      <Input 
                        value={email.smtp_port}
                        onChange={(e) => setEmail(prev => ({...prev, smtp_port: e.target.value}))}
                        placeholder="587"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Username</Label>
                    <Input 
                      value={email.smtp_username}
                      onChange={(e) => setEmail(prev => ({...prev, smtp_username: e.target.value}))}
                    />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input 
                      type="password"
                      value={email.smtp_password}
                      onChange={(e) => setEmail(prev => ({...prev, smtp_password: e.target.value}))}
                    />
                  </div>
                </>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{lang === 'ru' ? 'Email отправителя' : 'From Address'}</Label>
                  <Input
                    value={email.from_address}
                    onChange={(e) => setEmail(prev => ({...prev, from_address: e.target.value}))}
                    placeholder="noreply@yourcompany.com"
                  />
                </div>
                <div>
                  <Label>{lang === 'ru' ? 'Имя отправителя' : 'From Name'}</Label>
                  <Input
                    value={email.from_name}
                    onChange={(e) => setEmail(prev => ({...prev, from_name: e.target.value}))}
                    placeholder="CGC Services"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {lang === 'ru' ? 'Имя, которое увидит клиент' : 'Name visible to recipients'}
                  </p>
                </div>
              </div>

              <div>
                <Label>{lang === 'ru' ? 'Дневной лимит отправки' : 'Daily send limit'}</Label>
                <Input
                  type="number"
                  min="1"
                  max="10000"
                  value={email.daily_limit}
                  onChange={(e) => setEmail(prev => ({...prev, daily_limit: e.target.value}))}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {lang === 'ru'
                    ? 'Защита от превышения лимита провайдера (по умолчанию 299/день)'
                    : 'Protects against exceeding provider limits (default 299/day)'}
                </p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={() => handleSave('email', email)} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                  {lang === 'ru' ? 'Сохранить' : 'Save'}
                </Button>
                <Button variant="outline" onClick={() => handleTest('email')} disabled={testing === 'email' || !settings?.email?.configured}>
                  {testing === 'email' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {lang === 'ru' ? 'Отправить тест' : 'Send test'}
                </Button>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
